import { createSignal, onMount, Show, For } from "solid-js";
import { useApplication } from "@/context/app";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/shadcn/card";
import { Icon } from "@iconify-icon/solid";
import useCheckup from "../../services/useCheckup";
import CheckupCase from "./case";
import CheckupDiagnosis from "./diagnosis";
import CheckupTherapy from "./therapy";
import CheckupComplaint from "./complaint";
import CheckupTest from "./test";
import CheckupNoteHistory from "./checkup";
import CheckupSession from "./session";
import { LoadingButton } from "../../components/ui/Button";
import { Badge } from "@/components/ui/Badge";

export default function CheckupIndex() {
  const { setBreadcrumb, successToast, errorToast } = useApplication();
  const [activeTab, setActiveTab] = createSignal("notehistory");
  const [hasUnsavedChanges, setHasUnsavedChanges] = createSignal(false);
  const [changeTracker, setChangeTracker] = createSignal({
    notehistory: false,
    complaints: false,
    diagnosis: false,
    tests: false,
    therapies: false,
    sessions: false,
    case: false
  });

  // Store refs to each component's save function
  const componentSaveFunctions = {
    notehistory: null,
    case: null,
    diagnosis: null,
    therapies: null,
    complaints: null,
    tests: null,
    sessions: null,
  };

  const {
    appointment,
    fetching,
    onFetchAppointment,
    onAction,
    loading,
    setLoading,
    apppointmentCase,
    diagnoses,
    therapies,
    sessions,
    complaints,
    tests,
    histories,
    onSave,
    onUpdateNote,
    onPrintPrescription,
    prescriptionLoading,
  } = useCheckup();

  // Modified save function that calls all component save functions
  const handleSaveAll = async () => {
    setLoading(true);
    const errors = [];
    const successes = [];

    try {
      // Call each component's save function if it exists and has changes
      for (const [component, hasChanges] of Object.entries(changeTracker())) {
        if (hasChanges && componentSaveFunctions[component]) {
          try {
            await componentSaveFunctions[component]();
            successes.push(`${component} saved successfully`);
          } catch (error) {
            errors.push(`Failed to save ${component}: ${error.message}`);
          }
        }
      }

      // Show results
      if (successes.length > 0) {
        successToast(successes.join(", "));
      }
      if (errors.length > 0) {
        errorToast(errors.join(", "));
      }

      // Refresh data if all saves were successful
      if (errors.length === 0) {
        await onFetchAppointment();
        // Reset change tracker
        setChangeTracker({
          notehistory: false,
          complaints: false,
          diagnosis: false,
          tests: false,
          therapies: false,
          sessions: false,
          case: false
        });
      }
    } catch (error) {
      console.log(error);
      errorToast("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  onMount(async () => {
    await onFetchAppointment();
    setBreadcrumb([
      {
        name: "Home",
        link: "/home",
      },
      {
        name: "Checkup",
        current: true,
      },
    ]);
  });

  const tabs = [
    { id: "notehistory", label: "Checkup", icon: "mdi:note-text" },
    { id: "complaints", label: "Complaints", icon: "mdi:message-alert" },
    { id: "diagnosis", label: "Diagnosis", icon: "mdi:medical-bag" },
    { id: "tests", label: "Tests", icon: "mdi:test-tube" },
    { id: "therapies", label: "Therapies", icon: "mdi:medical-bag" },
    { id: "sessions", label: "Sessions", icon: "mdi:calendar-clock" },
  ];

  // Helper function to track changes for each component
  const trackChanges = (component, hasChanges) => {
    // Update the change tracker and check for unsaved changes in a single operation
    setChangeTracker(prev => {
      const updated = { ...prev, [component]: hasChanges };
      // Check if any component has unsaved changes
      setHasUnsavedChanges(Object.values(updated).some(Boolean));
      return updated;
    });
  };

  const renderTabContent = () => {
    switch (activeTab()) {
      case "notehistory":
        return (
          <div class="space-y-4">
            <CheckupNoteHistory 
              loading={loading()} 
              setLoading={setLoading} 
              appointment={appointment()} 
              histories={histories()}
              onUpdateNote={onUpdateNote} 
              onSave={onSave} 
              hideCard={true}
              hideSaveButton={true}
              onFormChange={(hasChanges) => trackChanges('notehistory', hasChanges)}
              onRegisterSave={(saveFn) => componentSaveFunctions.notehistory = saveFn}
            />
          </div>
        );
      case "complaints":
        return (
          <div class="space-y-4">
            <CheckupComplaint 
              loading={loading()} 
              setLoading={setLoading} 
              appointment={appointment()} 
              complaints={complaints()} 
              hideCard={true}
              hideSaveButton={true}
              onFormChange={(hasChanges) => trackChanges('complaints', hasChanges)}
              onRegisterSave={(saveFn) => componentSaveFunctions.complaints = saveFn}
            />
          </div>
        );
      case "tests":
        return (
          <div class="space-y-4">
            <CheckupTest 
              loading={loading()} 
              setLoading={setLoading} 
              appointment={appointment()} 
              tests={tests()} 
              hideCard={true}
              hideSaveButton={true}
              onFormChange={(hasChanges) => trackChanges('tests', hasChanges)}
              onRegisterSave={(saveFn) => componentSaveFunctions.tests = saveFn}
            />
          </div>
        );
      case "therapies":
        return (
          <div class="space-y-4">
            <CheckupTherapy 
              loading={loading()} 
              setLoading={setLoading} 
              appointment={appointment()} 
              therapies={therapies()} 
              hideCard={true}
              hideSaveButton={true}
              onFormChange={(hasChanges) => trackChanges('therapies', hasChanges)}
              onRegisterSave={(saveFn) => componentSaveFunctions.therapies = saveFn}
            />
          </div>
        );

      case "sessions":
        return (
          <div class="space-y-4">
            <CheckupSession 
              loading={loading()} 
              setLoading={setLoading} 
              appointment={appointment()} 
              sessions={sessions()} 
              hideCard={true}
              hideSaveButton={true}
              onFormChange={(hasChanges) => trackChanges('sessions', hasChanges)}
              onRegisterSave={(saveFn) => componentSaveFunctions.sessions = saveFn}
            />
          </div>
        );
      case "diagnosis":
        return (
          <div class="space-y-4">
            <CheckupDiagnosis 
              loading={loading()} 
              setLoading={setLoading} 
              appointment={appointment()} 
              onAction={onAction} 
              diagnoses={diagnoses()} 
              hideCard={true}
              hideSaveButton={true}
              onFormChange={(hasChanges) => trackChanges('diagnosis', hasChanges)}
              onRegisterSave={(saveFn) => componentSaveFunctions.diagnosis = saveFn}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div class="container mx-auto p-2">
        <div class="grid grid-cols-11 gap-3">
          {/* Main Content Area - 70% */}
          <div class="col-span-7">
            <Show
              when={!fetching()}
              fallback={
                <Card>
                  <CardContent class="p-6">
                    <div class="flex items-center justify-center">
                      <Icon
                        icon="mdi:loading"
                        class="animate-spin text-2xl text-gray-500"
                      />
                      <span class="ml-2">Loading appointment details...</span>
                    </div>
                  </CardContent>
                </Card>
              }
            >
              <Show
                when={appointment()}
                fallback={
                  <Card>
                    <CardContent class="p-6">
                      <div class="text-center text-gray-500">
                        <Icon
                          icon="mdi:calendar-remove"
                          class="text-4xl mx-auto mb-4"
                        />
                        <p>No current appointment found</p>
                      </div>
                    </CardContent>
                  </Card>
                }
              >
                <Card>
                  <CardHeader class="pb-3">
                    <div class="flex items-center justify-between border-b">
                      <div class="flex">
                        <For each={tabs}>
                          {(tab) => (
                            <button
                              class={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                                activeTab() === tab.id
                                  ? "border-primary text-primary"
                                  : "border-transparent text-muted-foreground hover:text-foreground"
                              }`}
                              onClick={() => setActiveTab(tab.id)}
                            >
                              <Icon icon={tab.icon} class="text-base" />
                              {tab.label}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent class="p-6 pt-0">
                    {renderTabContent()}
                  </CardContent>
                </Card>
              </Show>
            </Show>
          </div>

          {/* Sidebar - 30% */}
          <div class="col-span-4 space-y-4">
            <Show
              when={!fetching()}
              fallback={
                <Card>
                  <CardContent class="p-6">
                    <div class="flex items-center justify-center">
                      <Icon
                        icon="mdi:loading"
                        class="animate-spin text-2xl text-gray-500"
                      />
                      <span class="ml-2">Loading...</span>
                    </div>
                  </CardContent>
                </Card>
              }
            >
              <Show when={appointment()}>
                {/* Case Summary */}
                <CheckupCase 
                  loading={loading()} 
                  setLoading={setLoading} 
                  appointment={appointment()} 
                  onAction={onAction} 
                  case={apppointmentCase()} 
                  hideSaveButton={true}
                  onFormChange={(hasChanges) => trackChanges('case', hasChanges)}
                  onRegisterSave={(saveFn) => componentSaveFunctions.case = saveFn}
                />

                  <Show when={!fetching() && appointment()}>
                    <div class="px-3">
                      {/* Action Buttons */}
                      <div class="flex items-center flex-wrap justify-end">
                        
                        <div class="flex items-center flex-wrap gap-2 justify-end">
                          <LoadingButton
                            loading={loading()}
                            onClick={handleSaveAll}
                            variant="outline"
                            disabled={!hasUnsavedChanges()}
                          >
                            <div class="flex items-center gap-2 me-2">
                              <Badge variant={hasUnsavedChanges() ? "warning" : "success"}>
                                {hasUnsavedChanges() ? "Pending Changes" : "Up to date"}
                              </Badge>
                              Save All
                            </div>
                            
                          </LoadingButton>
                          <LoadingButton
                            loading={prescriptionLoading()}
                            onClick={onPrintPrescription}
                          >
                            Print Prescription
                          </LoadingButton>
                        </div>
                      </div>
                    </div>
                  </Show>
              </Show>
            </Show>
          </div>

        </div>
    </div>
  );
}
